import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    Typography,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    useAppSidePanelContextProvider
} from "../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import {
    useActionConfirmationDialog
} from "../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { PortfolioItemUpdateModel } from "../../../features/portfolio-info/types/PortfolioItemUpdateModel";
import { PortfolioItemModel } from "../../../features/portfolio-info/types/PortfolioItemModel";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { portfolioItemMappers } from "../../../features/portfolio-info/mappers/PortfolioItemMappers";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { AssetType } from "../../../shared/types/AssetType";
import { assetService } from "../../../shared/services/AssetService";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";
import { blackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";
import { ContextMenu, OrdersToolsPanel } from "../controls";
import { ApplicationTooltip, LineDelimiter } from "../views";
import { ContextMenuItem } from "../controls/context-menu/ContextMenuItem";
import { PortfolioProfile } from "../../../shared/types/PortfolioProfile";
import { ChangingParameters } from "../types";
import {
    ActionConfirmationDialog
} from "../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { PositionTooltipTitle } from "../../../features/portfolio-info/components/PositionTooltipTitle";
import { TableBodyRow, TableHeaderRow } from "../../shared/views";
import {
    ORDERS_TABLE_ROW_PADDING_Y,
    TABLE_EXPIRATION_EXP_CELL_FONT_SIZE,
    TABLE_SYMBOL_EXP_CELL_FONT_SIZE
} from "../../shared/constants/table.constants";
import { numberFormatters } from "../../shared/utils/numberFormatters";
import { OrderState } from "../../../shared/types/OrderState";
import type { MarketDataDto } from "../../../shared/types/MarketDataDto";

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import { OrderManagerDialog } from "../dialogs/order-manager-dialog/OrderManagerDialog";
import { AutoOrderManagerDialog } from "../dialogs/auto-order-manager-dialog/AutoOrderManagerDialog";
import { AutoOrderConfigurationModel } from "../../../features/portfolio-info/types/AutoOrderConfigurationModel";

type OrdersProps = {
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

export function Orders({
    isExpanded,
    setIsExpanded,
}: OrdersProps) {
    const {
        marketDataDto,
    } = useAppContext();
    
    const {
        selectedPortfolio,
        addFutureToPortfolio,
        updateItemInSelectedPortfolio,
        deleteItemFromSelectedPortfolio,
        portfolioProfile,
        updateSelectedPortfolio,
        fetchSelectedPortfolio,
        saveSelectedPortfolio,
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    } = useAppSidePanelContextProvider();

    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        confirmationMessage,
        submitButtonName,
    } = useActionConfirmationDialog();

    const [submitEditedPortfolioItem, setSubmitEditedPortfolioItem] = useState<(portfolioModel: PortfolioItemUpdateModel) => Promise<void>>();
    const [editingPortfolioItemModel, setEditingPortfolioItemModel] = useState<PortfolioItemUpdateModel | null>(null);
    const [isEditPortfolioItemDialogOpen, setIsEditPortfolioItemDialogOpen] = useState<boolean>(false);
    const [isNewPortfolioItemModel, setIsNewPortfolioItemModel] = useState<boolean>(false);
    const [autoOrderPortfolioItemModel, setAutoOrderPortfolioItemModel] = useState<PortfolioItemModel | null>(null);
    const [isAutoOrderManagerDialogOpen, setIsAutoOrderManagerDialogOpen] = useState<boolean>(false);
    const [hideLockOrders, setHideLockOrders] = useState<boolean>(true);
    const [selectedOrder, setSelectedOrder] = useState<PortfolioItemModel | null>(null);
    
    const portfolioItems = selectedPortfolio !== null
        ? selectedPortfolio.portfolioItems
        : [];
    
    const initialRowsPerPage = 15;
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [itemCount, setItemCount] = useState(0);
    const [page, setPage] = useState(0);

    const [isPortfolioChanged, setIsPortfolioChanged] = useState(false);

    useEffect(() => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            setHideLockOrders(true);
            return;
        }

        setHideLockOrders(!selectedPortfolio.portfolioSettings.showLockedOrders);
    }, [selectedPortfolio]);

    useEffect(() => {
        if (
            !isEditPortfolioItemDialogOpen ||
            isNewPortfolioItemModel ||
            editingPortfolioItemModel === null
        ) {
            return;
        }

        setSubmitEditedPortfolioItem(() => async (itemUpdateModel: PortfolioItemUpdateModel): Promise<void> => {
            const currentItemModel = selectedPortfolio?.portfolioItems.find(item =>
                itemUpdateModel.externalId > 0
                    ? item.externalId === itemUpdateModel.externalId
                    : item.internalId === itemUpdateModel.internalId);

            if (currentItemModel === undefined) {
                return;
            }

            portfolioItemMappers.map(itemUpdateModel, currentItemModel);
            await updateItemInSelectedPortfolioWrapper(true, true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedPortfolio,
        editingPortfolioItemModel?.externalId,
        editingPortfolioItemModel?.internalId,
        isEditPortfolioItemDialogOpen,
        isNewPortfolioItemModel,
    ]);

    const handleHideLockOrdersChange = (value: SetStateAction<boolean>): void => {
        const nextHideLockOrders = typeof value === 'function'
            ? value(hideLockOrders)
            : value;

        setHideLockOrders(nextHideLockOrders);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.showLockedOrders = !nextHideLockOrders;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };
    
    useEffect(() => {
        const itemCount = portfolioItems.filter(x => !hideLockOrders || x.state !== OrderState.Archived).length;
        setItemCount(itemCount);
        setPage(getLastPageIndex(rowsPerPage, itemCount));
        setIsPortfolioChanged(portfolioItems.some(x => x.isChanged));
    }, [portfolioItems, hideLockOrders]);
    
    const updateItemInSelectedPortfolioWrapper = async (
        refreshItemParameters: boolean, saveToStorage: boolean
    ): Promise<void> => {
        await updateItemInSelectedPortfolio(refreshItemParameters, saveToStorage);
        setIsPortfolioChanged(portfolioItems.some(x => x.isChanged));
    }
    
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };
    
    const getLastPageIndex = (rowsPerPage: number, itemCount: number): number => {
        const fullPageCount = rowsPerPage !== -1
            ? Math.floor(itemCount / rowsPerPage)
            : 1;
        
        return rowsPerPage !== -1
            ? fullPageCount * rowsPerPage === itemCount
                ? fullPageCount - 1
                : fullPageCount
            : 0;
    }
    
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rowsPerPage = +event.target.value;
        setPage(getLastPageIndex(rowsPerPage, itemCount));
        setRowsPerPage(rowsPerPage);
    };

    const handleItemArchiveStatusChanging = async (order: PortfolioItemModel): Promise<void> => {
        order.state = order.state === OrderState.Archived
            ? OrderState.Active
            : OrderState.Archived;

        order.locked = order.state === OrderState.Archived;

        await saveSelectedPortfolio();
        
        // await updateItemInSelectedPortfolioWrapper(true, true);
    };

    const handleItemActiveStatusChanging = async (event: React.ChangeEvent<HTMLInputElement>, item: PortfolioItemModel): Promise<void> => {
        if (item.state === OrderState.Archived) {
            return;
        }

        item.isActive = event.target.checked;
        item.state = event.target.checked
            ? OrderState.Active
            : OrderState.Disabled;

        await updateItemInSelectedPortfolioWrapper(true, false);
    };
    
    const handleEditPortfolioItem = (itemModel: PortfolioItemModel, closeContextMenu?: () => void | null) => {
        if (closeContextMenu)
            closeContextMenu();
        
        setEditingPortfolioItemModel(portfolioItemMappers.mapForEditing(itemModel));
        setIsNewPortfolioItemModel(false);
        setSubmitEditedPortfolioItem(() => async (itemUpdateModel: PortfolioItemUpdateModel): Promise<void> => {
            const currentItemModel = selectedPortfolio?.portfolioItems.find(item =>
                item.externalId === itemUpdateModel.externalId ||
                item.internalId === itemUpdateModel.internalId);

            if (currentItemModel === undefined) {
                return;
            }

            portfolioItemMappers.map(itemUpdateModel, currentItemModel);
            await updateItemInSelectedPortfolioWrapper(true, true);
        });
        setIsEditPortfolioItemDialogOpen(true);
    };
    
    const deleteEditingItem = () => async () =>{
        setSelectedOrder(null);
        await deleteItemFromSelectedPortfolio(editingPortfolioItemModel!.internalId);
    }
    
    const getOpenPrice = (): number => {
        return selectedPortfolio?.portfolioSettings?.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice
            : marketDataDto?.BaseAsset.Price ?? 0
    }
    
    const openPrice = getOpenPrice();
    
    const handleAddPortfolioItem = () => {
        const underlyingAsset = marketDataDto?.BaseAsset;
        const size = 1;
        
        const newItemModel: PortfolioItemModel = new PortfolioItemModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            isActive: true,
            locked: false,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: null,
            impliedVolatilityAtOpen: null,
            customImpliedVolatility: null,
            symbol: `${underlyingAsset!.BaseAsset}/USDT`,
            size: size,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: null,
            type: AssetType.Futures,
            strike: null,
            underlyingPriceAtOpen: null,
            delta: Math.sign(size),
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: null,
            underlyingPriceForPricing: openPrice,
        });
        
        setEditingPortfolioItemModel(portfolioItemMappers.mapForEditing(newItemModel));
        setIsNewPortfolioItemModel(true);
        setSubmitEditedPortfolioItem(() => async (itemUpdateModel: PortfolioItemUpdateModel): Promise<void> => {
            portfolioItemMappers.map(itemUpdateModel, newItemModel);
            await addFutureToPortfolio(newItemModel);
        });
        setIsEditPortfolioItemDialogOpen(true);
    };
    
    const handleOnOrderClick = (order: PortfolioItemModel) => {
        setSelectedOrder(order);
    };
    
    const handleUpdateCreationDateTime = async (order: PortfolioItemModel, closeContextMenu: () => void) => {
        closeContextMenu();
        order.openedPositionAt = dateTimeConversionService.getCurrentUtcDate();
        await updateItemInSelectedPortfolio(false, true);
    }
    
    const handleAddClosingOrder = async (order: PortfolioItemModel, closeContextMenu: () => void) => {
        closeContextMenu();
        await cloneOrder(order, true);
    };

    const handleCloneOrder = async (order: PortfolioItemModel, closeContextMenu: () => void) => {
        closeContextMenu();
        await cloneOrder(order, false);
    };

    const cloneOrder = async (order: PortfolioItemModel, shouldInvertOrderSize: boolean) => {
        if (
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = () => {
            if (assetService.isOption(order)) {
                const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                    .getTimeToExpirationInShareOfYear(order.expireAt!);

                return blackScholesModelCalculationService.getPremium(
                    order.type, underlyingPrice, order.strike!,
                    timeToExpirationInShareOfYear, order.markImpliedVolatility!);
            } else {
                return underlyingPrice;
            }
        }

        const openPrice = getOpenPrice();

        const newItemModel: PortfolioItemModel = new PortfolioItemModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            isActive: order.isActive,
            locked: false,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: order.expireAt,
            impliedVolatilityAtOpen: order.markImpliedVolatility,
            customImpliedVolatility: order.markImpliedVolatility,
            symbol: order.symbol,
            size: shouldInvertOrderSize ? -order.size : order.size,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: order.markImpliedVolatility,
            type: order.type,
            strike: order.strike,
            underlyingPriceAtOpen: underlyingPrice,
            delta: 0,
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: order.expirationPrice,
            dateForPricing: order.dateForPricing,
            impliedVolatilityForPricing: order.impliedVolatilityForPricing,
            underlyingPriceForPricing: order.underlyingPriceForPricing,
        });

        await addFutureToPortfolio(newItemModel);
    };

    const handleRemoveOrder = async (order: PortfolioItemModel, closeContextMenu: () => void) => {
        closeContextMenu();
        
        if (order.locked || order.isActive ) {
            return;
        }

        await deleteItemFromSelectedPortfolio(order.internalId);        
    };

    const isAutoOrderAvailable = (order: PortfolioItemModel): boolean =>
        assetService.isOption(order) && order.externalId !== 0;

    const handleOpenAutoOrderManager = (order: PortfolioItemModel, closeContextMenu: () => void): void => {
        closeContextMenu();

        if (!isAutoOrderAvailable(order)) {
            return;
        }

        setAutoOrderPortfolioItemModel(order);
        setIsAutoOrderManagerDialogOpen(true);
    };

    const handleAutoOrderConfigurationSaved = (configuration: AutoOrderConfigurationModel): void => {
        const order = selectedPortfolio?.portfolioItems.find(
            item => item.externalId === configuration.portfolioItemId) ?? null;

        if (order !== null) {
            order.autoOrderConfiguration = configuration;
        }

        if (autoOrderPortfolioItemModel?.externalId === configuration.portfolioItemId) {
            setAutoOrderPortfolioItemModel(order ?? autoOrderPortfolioItemModel);
        }

        if (editingPortfolioItemModel?.externalId === configuration.portfolioItemId) {
            setEditingPortfolioItemModel({
                ...editingPortfolioItemModel,
                autoOrderConfiguration: configuration,
            });
        }

        void updateSelectedPortfolio();
    };

    const reloadEditingPortfolioItem = async (marketData: MarketDataDto | null): Promise<PortfolioItemUpdateModel | null> => {
        if (editingPortfolioItemModel === null || editingPortfolioItemModel.externalId <= 0) {
            return null;
        }

        const reloadedPortfolio = await fetchSelectedPortfolio(marketData);
        const reloadedOrder = reloadedPortfolio?.portfolioItems.find(
            item => item.externalId === editingPortfolioItemModel.externalId) ?? null;

        if (reloadedOrder === null) {
            return null;
        }

        const reloadedEditingModel = portfolioItemMappers.mapForEditing(reloadedOrder);
        setEditingPortfolioItemModel(reloadedEditingModel);

        return reloadedEditingModel;
    };
    
    const getPortfolioCurrentDeltaAtPrice = (
        portfolioProfile: PortfolioProfile,
        price: number
    ): number | null => {
        const records = portfolioProfile.data;

        for (let i = 0; i < records.length - 1; i++) {
            if (records[i].price <= price && records[i + 1].price > price ||
                records[i].price >= price && records[i + 1].price < price)
            {
                return records[i].currentDelta;
            }
        }

        return null;
    }
    
    const getCurrentPortfolioDeltaNeutralizingOrderSize = () =>
        mathCalculationService.round(
            0 - (getPortfolioCurrentDeltaAtPrice(portfolioProfile, openPrice) ?? 0),
            3);

    const deltaNeutralizingOrderSize = getCurrentPortfolioDeltaNeutralizingOrderSize();
        
    const handleAddDeltaNeutralizingOrder = async (closeContextMenu: () => void) => {
        closeContextMenu();
        
        if (
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null ||
            portfolioProfile === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = (): number => {
            return selectedPortfolio?.portfolioSettings?.useCustomPrice
                ? selectedPortfolio.portfolioSettings.customPrice
                : marketDataDto?.BaseAsset.Price ?? 0
        }
        
        const underlyingAsset = marketDataDto?.BaseAsset;
        const openPrice = getOpenPrice();

        const newItemModel: PortfolioItemModel = new PortfolioItemModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            isActive: true,
            locked: false,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: null,
            impliedVolatilityAtOpen: null,
            customImpliedVolatility: null,
            symbol: `${underlyingAsset!.BaseAsset}/USDT`,
            size: deltaNeutralizingOrderSize,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: null,
            type: AssetType.Futures,
            strike: null,
            underlyingPriceAtOpen: underlyingPrice,
            delta: deltaNeutralizingOrderSize,
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: null,
            underlyingPriceForPricing: underlyingPrice,
        });

        await addFutureToPortfolio(newItemModel);
    };
    
    const buildChangingParameters = (): ChangingParameters => {
        return {
            selectedOrder,
            marketDataDto,
            portfolioItems,
            updateItemInSelectedPortfolio: updateItemInSelectedPortfolioWrapper
        };
    }
    
    return (
        <Stack
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                paddingTop: '8px',
            }}
        >
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                submitButtonName={submitButtonName}
                confirmedParentAction={confirmedAction}
            />
            <OrderManagerDialog
                isOpen={isEditPortfolioItemDialogOpen}
                setIsOpen={setIsEditPortfolioItemDialogOpen}
                submitAction={submitEditedPortfolioItem}
                deleteAction={deleteEditingItem}
                editingModel={editingPortfolioItemModel}
                isNew={isNewPortfolioItemModel}
                icon={isNewPortfolioItemModel ? <AddBoxIcon /> : <EditIcon />}
                onAutoOrderConfigurationSaved={handleAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                reloadEditingOrder={reloadEditingPortfolioItem}
            />
            <AutoOrderManagerDialog
                isOpen={isAutoOrderManagerDialogOpen}
                setIsOpen={setIsAutoOrderManagerDialogOpen}
                portfolioItemId={autoOrderPortfolioItemModel?.externalId ?? 0}
                optionOrderSymbol={autoOrderPortfolioItemModel?.symbol ?? ""}
                optionOrderSize={autoOrderPortfolioItemModel?.size ?? 0}
                optionImpliedVolatility={autoOrderPortfolioItemModel?.markImpliedVolatility ?? null}
                underlyingPrice={marketDataDto?.UnderlyingPrice ?? marketDataDto?.BaseAsset.Price ?? null}
                autoOrderConfiguration={autoOrderPortfolioItemModel?.autoOrderConfiguration ?? null}
                onConfigurationSaved={handleAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                icon={<AutofpsSelectIcon />}
            />
            <OrdersToolsPanel
                hideLockOrders={hideLockOrders}
                setHideLockOrders={handleHideLockOrdersChange}
                isPortfolioChanged={isPortfolioChanged}
                setIsPortfolioChanged={setIsPortfolioChanged}
                changingParameters={buildChangingParameters()}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
            />
            <LineDelimiter/>
            <Paper
                sx={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 0,
                }}
            >
                <TableContainer
                    sx={{
                        flex: '1',
                        padding: '0px',
                        margin: '0px',
                        overflowX: 'auto',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}
                    >
                        <TableHead>
                            <TableHeaderRow>
                                <ApplicationTooltip
                                    title="If marked, the order has been sent to the exchange and approved"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>R</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="If checked, the order is archived"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>A</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="If checked, the order is included in the portfolio profile calculation"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>S</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Edit order"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>E</TableCell>
                                </ApplicationTooltip>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Symbol</TableCell>
                                <TableCell>Exp</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Volume</TableCell>
                                <ApplicationTooltip
                                    title="Implied volatility at the time of execution"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>IV,%</TableCell>
                                </ApplicationTooltip>
                                <TableCell>Delta</TableCell>
                                <TableCell>Gamma</TableCell>
                                <TableCell>Vega</TableCell>
                                <TableCell>Theta</TableCell>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Note</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {portfolioItems
                                .filter(order => hideLockOrders ? !order.locked : true)
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .sort((x: PortfolioItemModel, y: PortfolioItemModel) =>
                                    new Date(x.openedPositionAt).getTime() >
                                    new Date(y.openedPositionAt).getTime()
                                        ? 1
                                        : -1)
                                .map((order) => (
                                    <ContextMenu<PortfolioItemModel>
                                        renderItems={(order, closeContextMenu) => (
                                            <>
                                                <ContextMenuItem
                                                    onClick={() => handleEditPortfolioItem(order, closeContextMenu)}
                                                >
                                                    Edit
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleUpdateCreationDateTime(order, closeContextMenu)}
                                                    disabled={order.locked || order.isActive}
                                                >
                                                    Update creation time
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleAddClosingOrder(order, closeContextMenu)}
                                                >
                                                    Add closing order
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleCloneOrder(order, closeContextMenu)}
                                                >
                                                    Add cloned order
                                                </ContextMenuItem>
                                                 <ApplicationTooltip
                                                     placement='right-start'
                                                     title={
                                                        (order.locked ?? true) || order.isActive
                                                            ? "Only inactive orders can be removed"
                                                            : null
                                                     }
                                                 >
                                                     <span>
                                                         <ContextMenuItem
                                                             onClick={() => handleRemoveOrder(order, closeContextMenu)}
                                                             disabled={(order.locked ?? true) || order.isActive}
                                                         >
                                                            Remove order
                                                         </ContextMenuItem>
                                                     </span>
                                                 </ApplicationTooltip>
                                                <ContextMenuItem
                                                    onClick={() => handleAddDeltaNeutralizingOrder(closeContextMenu)}
                                                    disabled={deltaNeutralizingOrderSize === 0}
                                                >
                                                    {
                                                        deltaNeutralizingOrderSize !== 0
                                                            ? `Add delta-neutralizing order: ${deltaNeutralizingOrderSize}`
                                                            : 'Add delta-neutralizing order'
                                                    }
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => handleOpenAutoOrderManager(order, closeContextMenu)}
                                                    disabled={!isAutoOrderAvailable(order)}
                                                >
                                                    Auto Order Manager...
                                                </ContextMenuItem>
                                            </>
                                        )}
                                    >
                                        {({onContextMenu}) => (
                                            <TableBodyRow
                                                onClick={() => handleOnOrderClick(order)}
                                                onContextMenu={
                                                    (event: React.MouseEvent) => {
                                                        setSelectedOrder(order);
                                                        onContextMenu(event, order)
                                                    }
                                                }
                                                key={order.internalId}
                                                paddingY={ORDERS_TABLE_ROW_PADDING_Y}
                                                style={{
                                                    ...(selectedOrder && selectedOrder.externalId === order.externalId
                                                        ? {backgroundColor: 'rgba(255,224,122,0.5)'}
                                                        : {}),
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: '0px',
                                                            minWidth: '25px'
                                                        },
                                                    }}
                                                >
                                                    {
                                                        order.exchangeOrderId !== null &&
                                                            <IconButton
                                                                color="default"
                                                                size="small"
                                                                sx={{
                                                                    padding: '0px',
                                                                    transform: 'scale(0.85)'
                                                                }}
                                                            >
                                                                <AccountBalanceIcon />
                                                            </IconButton>
                                                    }
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={
                                                        order.state === OrderState.Disabled
                                                            ? "Inactive orders can’t be archived"
                                                            : null
                                                    }
                                                    disableInteractive
                                                    offset={[0, -7]}
                                                >
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            "&&": {
                                                                paddingX: "2px",
                                                            },
                                                        }}
                                                    >
                                                        {
                                                            order.state === OrderState.Archived
                                                                ?
                                                                    <IconButton
                                                                        color="default"
                                                                        size="small"
                                                                        sx={{padding: '0px'}}
                                                                        onClick={() => handleItemArchiveStatusChanging(order)}
                                                                    >
                                                                        <LockIcon />
                                                                    </IconButton>
                                                                :
                                                                    <IconButton
                                                                        color="default"
                                                                        size="small"
                                                                        sx={{padding: '0px'}}
                                                                        onClick={() => handleItemArchiveStatusChanging(order)}
                                                                        disabled={order.state === OrderState.Disabled}
                                                                    >
                                                                        <LockOpenIcon />
                                                                    </IconButton>
                                                        }
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: "2px",
                                                        },
                                                    }}
                                                >
                                                    <ApplicationTooltip
                                                        title={
                                                            order.state === OrderState.Archived
                                                                ? "Archived orders can’t be set to inactive"
                                                                : null
                                                        }
                                                        disableInteractive
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                color="default"
                                                                checked={order.isActive}
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleItemActiveStatusChanging(event, order)}
                                                                sx={{
                                                                    padding: '0px',
                                                                    '&.Mui-disabled': {
                                                                        color: 'rgba(0, 0, 0, 0.25)',
                                                                    },
                                                                }}
                                                                disabled={order.state === OrderState.Archived}
                                                            />
                                                        </span>
                                                    </ApplicationTooltip>
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: '2px',
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        color="warning"
                                                        size="small"
                                                        sx={{padding: '0px'}}
                                                        onClick={() => handleEditPortfolioItem(order)}
                                                    >
                                                        <EditIcon/>
                                                    </IconButton>
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={<PositionTooltipTitle portfolioItemModel={order}/>}
                                                    maxWidth='450px'
                                                    offset={[0, -7]}
                                                    disableInteractive
                                                >
                                                    <TableCell align="left">
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: TABLE_SYMBOL_EXP_CELL_FONT_SIZE,
                                                                textOverflow: 'ellipsis',
                                                                ...(order.isChanged
                                                                    ? {color: 'rgb(213,1,1)'}
                                                                    : {}),
                                                            }}
                                                        >
                                                            {portfolioItemMappers.getSymbolName(order.symbol, order.type)}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <TableCell align="right">
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontSize: TABLE_EXPIRATION_EXP_CELL_FONT_SIZE,
                                                            textOverflow: 'ellipsis',
                                                            ...(order.isChanged
                                                                ? {color: 'rgb(213,1,1)'}
                                                                : {}),
                                                        }}
                                                    >
                                                        {
                                                            assetService.isOption(order) && !assetService.isOptionExpired(order)
                                                                ? dateTimeConversionService.getExpirationPeriodInfoText(order.expireAt)
                                                                : null
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format3(order.size)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format0(order.openPrice)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {numberFormatters.format0(-(order.size * order.openPrice))}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {order.impliedVolatilityAtOpen !== null
                                                        ? numberFormatters.format2(order.impliedVolatilityAtOpen * 100)
                                                        : ''
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {

                                                        assetService.isOption(order)
                                                            ? !assetService.isOptionExpired(order)
                                                                ? numberFormatters.format3(order.delta)
                                                                : ''
                                                            : numberFormatters.format3(order.delta)
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.gamma !== null && assetService.isOption(order)
                                                            ? numberFormatters.format6(order.gamma)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.vega !== null && assetService.isOption(order)
                                                            ? numberFormatters.format2(order.vega)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{
                                                        ...(order.isChanged
                                                            ? {color: 'rgb(213,1,1)'}
                                                            : {}),
                                                    }}
                                                >
                                                    {
                                                        order.theta !== null && assetService.isOption(order)
                                                            ? numberFormatters.format0(order.theta)
                                                            : null
                                                    }
                                                </TableCell>
                                                <ApplicationTooltip
                                                    offset={[0, -7]}
                                                    title={order.comment}
                                                >
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            maxWidth: '100px',
                                                            ...(order.isChanged
                                                                ? {color: 'rgb(213,1,1)'}
                                                                : {}),
                                                        }}
                                                    >
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                flex: 1,
                                                                fontSize: '14px',
                                                                textOverflow: 'ellipsis',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {order.comment}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                            </TableBodyRow>
                                        )}
                                    </ContextMenu>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Stack direction="row" justifyContent="space-between">
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            fontSize: '14px',
                            marginBottom: '-6px'
                        }}
                    >
                        <IconButton
                            color="success"
                            sx={{
                                padding: '0px',
                                paddingLeft: '8px',
                                marginBottom: '-2px'
                            }}
                            onClick={handleAddPortfolioItem}
                        >
                            <AddBoxIcon/>
                        </IconButton>
                        <Box sx={{paddingLeft: '5px', fontWeight: 'bold'}}>
                            New order
                        </Box>
                    </Stack>
                    <TablePagination
                        showFirstButton
                        showLastButton
                        rowsPerPageOptions={[8, 15, 20, 25, 100]}
                        component="div"
                        count={itemCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            height: 'auto',
                            marginBottom: '-10px',
                            marginLeft: '-10px'
                        }}
                    />
                </Stack>
            </Paper>
        </Stack>
    );
}
